import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, X, SkipForward, Mic2, ListMusic, Lock, Sparkles } from "lucide-react";
import { usePaywall } from "@/contexts/PaywallContext";

/**
 * PaywallModal — full-screen brutalist takeover.
 *
 * Visual language (1o1 theme)
 * ───────────────────────────
 *   • Pitch-black backdrop with vermilion radial glow
 *   • Massive Cabinet Grotesk headline ("PAUSED.")
 *   • Numeric kicker ("01" "02" "03") on the feature breakdown card
 *   • Diagonal vermilion noise band behind the headline for editorial bite
 *   • Sharp-edged primary CTA (rounded-sm), secondary "Maybe later" as plain text
 *
 * Reason-specific copy lives in the ``REASONS`` map below — keep it
 * short, declarative, and emotionally specific to the moment the user
 * hit the wall.
 */

const REASONS = {
  skip: {
    icon: SkipForward,
    kicker: "Skip limit",
    headline: "PAUSED.",
    subhead: "You're out of skips for the hour.",
    body: "Free listeners get 6 skips an hour. Go Premium for unlimited skipping — chase the mood, not the timer.",
    cta: "Get unlimited skips",
  },
  lyrics: {
    icon: Mic2,
    kicker: "Premium feature",
    headline: "SING IT.",
    subhead: "Lyrics are a Premium feature.",
    body: "Pull up time-synced lyrics for every song in the catalog, plus artist-submitted originals you won't find anywhere else.",
    cta: "Unlock lyrics",
  },
  "queue-reorder": {
    icon: ListMusic,
    kicker: "Premium feature",
    headline: "YOUR ORDER.",
    subhead: "Re-arrange the queue with Premium.",
    body: "Drag any track to where you want it. Build the perfect run — first track to last — without restarting your night.",
    cta: "Unlock queue control",
  },
  "queue-add": {
    icon: ListMusic,
    kicker: "Premium feature",
    headline: "QUEUE IT.",
    subhead: "Build a custom queue with Premium.",
    body: "Stack tracks on the fly across albums, artists and playlists. Free users can only follow the album order.",
    cta: "Unlock queue control",
  },
  generic: {
    icon: Sparkles,
    kicker: "Premium",
    headline: "GO PREMIUM.",
    subhead: "This is a Premium feature.",
    body: "Listener Premium is $10/mo and unlocks the whole 1o1sound experience — unlimited skips, lyrics, queue control, no ads.",
    cta: "Get Premium",
  },
};

const FeatureRow = ({ num, label, locked = true }) => (
  <li className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-b-0">
    <span className="text-[10px] font-black tracking-[0.22em] text-vermilion w-6">{num}</span>
    <Lock size={13} className={locked ? "text-vermilion" : "text-white/30"} strokeWidth={2.4} />
    <span className="text-sm text-white/85 font-medium">{label}</span>
  </li>
);

const PaywallModal = () => {
  const { isOpen, reason, closePaywall } = usePaywall();

  // ESC key dismiss
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closePaywall(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closePaywall]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const cfg = REASONS[reason] || REASONS.generic;
  const Icon = cfg.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="paywall-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-xl flex items-center justify-center px-4 sm:px-8 py-10 overflow-y-auto"
          onClick={closePaywall}
        >
          {/* Editorial vermilion glow — radial behind the card */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-50"
            style={{
              background:
                "radial-gradient(60% 50% at 50% 30%, rgba(232,69,37,0.25) 0%, rgba(232,69,37,0) 70%)",
            }}
          />

          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.99 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="relative w-full max-w-2xl bg-gradient-to-b from-[#0d0d0d] via-[#0a0a0a] to-black border border-white/10 rounded-2xl shadow-[0_40px_120px_-20px_rgba(232,69,37,0.35)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Diagonal vermilion stripe — brutalist editorial mark */}
            <div
              aria-hidden
              className="absolute -top-10 -right-10 w-72 h-72 rotate-12 opacity-20"
              style={{
                background:
                  "repeating-linear-gradient(45deg, #E84525 0 6px, transparent 6px 14px)",
              }}
            />

            {/* Close (X) */}
            <button
              data-testid="paywall-close-btn"
              onClick={closePaywall}
              className="absolute top-4 right-4 z-10 w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-white/70 hover:text-white transition"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="relative px-6 sm:px-10 pt-10 pb-8">
              {/* Kicker + icon */}
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-sm bg-vermilion flex items-center justify-center shadow-[0_8px_24px_-4px_rgba(232,69,37,0.6)]">
                  <Icon size={18} strokeWidth={2.4} className="text-white" />
                </div>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-black text-vermilion">
                  {cfg.kicker}
                </p>
              </div>

              {/* Headline + sub */}
              <h2
                data-testid="paywall-headline"
                className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.92] mb-3 text-white"
              >
                {cfg.headline}
              </h2>
              <p className="font-display text-lg sm:text-xl font-bold text-white/85 mb-2">
                {cfg.subhead}
              </p>
              <p className="text-sm sm:text-base text-white/55 leading-relaxed max-w-xl">
                {cfg.body}
              </p>

              {/* Feature breakdown — what you get */}
              <div className="mt-7 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mb-1.5">
                  What you unlock
                </p>
                <ul>
                  <FeatureRow num="01" label="Unlimited skips" />
                  <FeatureRow num="02" label="Time-synced lyrics" />
                  <FeatureRow num="03" label="Queue control & reorder" />
                  <FeatureRow num="04" label="No audio ads" />
                  <FeatureRow num="05" label="Higher-quality streams" />
                </ul>
              </div>

              {/* CTA row */}
              <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link
                  to="/premium"
                  data-testid="paywall-cta-premium"
                  onClick={closePaywall}
                  className="group flex-1 inline-flex items-center justify-center gap-2 bg-vermilion hover:bg-vermilion-hover text-white text-sm font-black uppercase tracking-[0.12em] px-6 py-4 rounded-sm transition shadow-[0_14px_40px_-10px_rgba(232,69,37,0.6)]"
                >
                  <Crown size={16} className="group-hover:rotate-[8deg] transition" />
                  {cfg.cta}
                  <span className="opacity-60 text-[11px] font-bold">· $10/mo</span>
                </Link>
                <button
                  data-testid="paywall-cta-dismiss"
                  onClick={closePaywall}
                  className="inline-flex items-center justify-center text-xs uppercase tracking-[0.18em] font-bold text-white/45 hover:text-white/80 transition px-4 py-3"
                >
                  Maybe later
                </button>
              </div>

              <p className="mt-4 text-[11px] text-white/30">
                2 months free for new listeners · cancel anytime
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaywallModal;
